// AuthContext for self-hosted credentials authentication
// Provides user state, organization context, and platform roles

import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UserWithRoles } from '@shared/schema';
import { logout } from '@/lib/authUtils';

export interface OrganizationWithRole {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  avatar_url?: string | null;
  website?: string | null;
  status?: string | null;
  industry?: string | null;
  timezone?: string | null;
  license_type?: string | null;
  license_seats?: number | null;
  license_expiry?: string | null;
  owner_id?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  role: string;
}

export interface PlatformRoles {
  isPlatformOwner: boolean;
  isPlatformAdmin: boolean;
  isPlatformStaff: boolean;
  isSupportViewer: boolean;
}

export type WorkspaceType = 'personal' | 'organization';

export interface Workspace {
  type: WorkspaceType;
  organizationId: string | null;
  organizationName: string | null;
}

interface OrganizationsResponse {
  organizations: OrganizationWithRole[];
  total: number;
  limit: number;
  offset: number;
}

const CURRENT_ORG_STORAGE_KEY = 'pathwork_current_org_id';
const WORKSPACE_TYPE_STORAGE_KEY = 'pathwork_workspace_type';

interface AuthContextType {
  user: UserWithRoles | null;
  userOrganizations: OrganizationWithRole[];
  currentOrganization: OrganizationWithRole | null;
  setCurrentOrganization: (org: OrganizationWithRole | null) => void;
  loading: boolean;
  signOut: () => void;
  canManageOrganizations: boolean;
  isSuperAdmin: boolean;
  platformRoles: PlatformRoles;
  currentWorkspace: Workspace;
  setCurrentWorkspace: (workspace: Workspace) => void;
  switchToPersonalWorkspace: () => void;
  switchToOrganizationWorkspace: (org: OrganizationWithRole) => void;
}

const defaultPlatformRoles: PlatformRoles = {
  isPlatformOwner: false,
  isPlatformAdmin: false,
  isPlatformStaff: false,
  isSupportViewer: false,
};

const defaultWorkspace: Workspace = {
  type: 'personal',
  organizationId: null,
  organizationName: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userOrganizations, setUserOrganizations] = useState<OrganizationWithRole[]>([]);
  const [currentOrganization, setCurrentOrganizationState] = useState<OrganizationWithRole | null>(null);
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace>(defaultWorkspace);

  // Fetch authenticated user from backend (includes organization roles)
  const { data: user, isLoading: userLoading, error: userError, isFetched: userFetched } = useQuery<UserWithRoles>({
    queryKey: ["/api/auth/user"],
    retry: false,
  });

  // If 401 error, user is not authenticated - this is expected for logged-out users
  // Check for any error that indicates unauthorized access
  const isUnauthorized = userError && (
    /401/.test((userError as Error).message) || 
    /Unauthorized/.test((userError as Error).message)
  );
  
  // Determine if we're still loading auth state
  // Loading is false if: query finished (fetched) OR we have an error
  const isAuthLoading = !userFetched && userLoading && !userError;

  // Fetch user's organizations when user is authenticated
  const { data: organizationsResponse } = useQuery<OrganizationsResponse>({
    queryKey: ["/api/organizations"],
    enabled: !!user && !isUnauthorized,
    retry: false,
  });

  // Update organizations state when data changes
  useEffect(() => {
    if (organizationsResponse?.organizations) {
      setUserOrganizations(organizationsResponse.organizations);
      
      // Restore current org from localStorage or default to first org
      const savedOrgId = localStorage.getItem(CURRENT_ORG_STORAGE_KEY);
      const savedWorkspaceType = localStorage.getItem(WORKSPACE_TYPE_STORAGE_KEY) as WorkspaceType | null;
      const orgs = organizationsResponse.organizations;
      
      if (orgs.length > 0) {
        const savedOrg = savedOrgId ? orgs.find(org => org.id === savedOrgId) : null;
        setCurrentOrganizationState(savedOrg || orgs[0]);
        
        // Restore workspace state
        if (savedWorkspaceType === 'organization' && savedOrg) {
          setCurrentWorkspaceState({
            type: 'organization',
            organizationId: savedOrg.id,
            organizationName: savedOrg.name,
          });
        } else {
          setCurrentWorkspaceState(defaultWorkspace);
        }
      } else {
        setCurrentOrganizationState(null);
        setCurrentWorkspaceState(defaultWorkspace);
      }
    } else {
      setUserOrganizations([]);
      setCurrentOrganizationState(null);
      setCurrentWorkspaceState(defaultWorkspace);
    }
  }, [organizationsResponse]);

  const setCurrentOrganization = useCallback((org: OrganizationWithRole | null) => {
    setCurrentOrganizationState(org);
    if (org) {
      localStorage.setItem(CURRENT_ORG_STORAGE_KEY, org.id);
    } else {
      localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
    }
  }, []);

  const setCurrentWorkspace = useCallback((workspace: Workspace) => {
    setCurrentWorkspaceState(workspace);
    localStorage.setItem(WORKSPACE_TYPE_STORAGE_KEY, workspace.type);
    if (workspace.type === 'organization' && workspace.organizationId) {
      localStorage.setItem(CURRENT_ORG_STORAGE_KEY, workspace.organizationId);
      const org = userOrganizations.find(o => o.id === workspace.organizationId);
      if (org) {
        setCurrentOrganizationState(org);
      }
    }
  }, [userOrganizations]);

  const switchToPersonalWorkspace = useCallback(() => {
    setCurrentWorkspaceState(defaultWorkspace);
    localStorage.setItem(WORKSPACE_TYPE_STORAGE_KEY, 'personal');
  }, []);

  const switchToOrganizationWorkspace = useCallback((org: OrganizationWithRole) => {
    setCurrentWorkspaceState({
      type: 'organization',
      organizationId: org.id,
      organizationName: org.name,
    });
    setCurrentOrganizationState(org);
    localStorage.setItem(WORKSPACE_TYPE_STORAGE_KEY, 'organization');
    localStorage.setItem(CURRENT_ORG_STORAGE_KEY, org.id);
  }, []);

  const signOut = () => {
    localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
    localStorage.removeItem(WORKSPACE_TYPE_STORAGE_KEY);
    logout();
  };

  // Computed values from user data
  const canManageOrganizations = user?.canManageOrganizations ?? false;
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  
  // Platform roles from API response (API returns snake_case, we access with both for compatibility)
  const rawPlatformRoles = (user as any)?.platformRoles ?? (user as any)?.platform_roles;
  const platformRoles: PlatformRoles = rawPlatformRoles 
    ? {
        isPlatformOwner: rawPlatformRoles.isPlatformOwner ?? rawPlatformRoles.is_platform_owner ?? false,
        isPlatformAdmin: rawPlatformRoles.isPlatformAdmin ?? rawPlatformRoles.is_platform_admin ?? false,
        isPlatformStaff: rawPlatformRoles.isPlatformStaff ?? rawPlatformRoles.is_platform_staff ?? false,
        isSupportViewer: rawPlatformRoles.isSupportViewer ?? rawPlatformRoles.is_support_viewer ?? false,
      }
    : defaultPlatformRoles;

  const value: AuthContextType = {
    user: isUnauthorized ? null : (user || null),
    userOrganizations,
    currentOrganization,
    setCurrentOrganization,
    loading: isAuthLoading,
    signOut,
    canManageOrganizations,
    isSuperAdmin,
    platformRoles,
    currentWorkspace,
    setCurrentWorkspace,
    switchToPersonalWorkspace,
    switchToOrganizationWorkspace,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
